import { inject, Injectable } from '@angular/core';

import { AuthService } from './api/auth/auth.service';
import { SnackbarService } from './snackbar.service';
import { UserService } from './api/user/user.service';
import { UserDataService } from './data/user-data.service';
import { FeatureFlagService } from './feature-flag.service';
import { WebsocketService } from './websocket/websocket.service';
import { SessionTimeoutService } from './session-timeout.service';
import { ConfigurationsService } from './api/configurations/configurations.service';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';
import { TenantService } from './api/tenant/tenant.service';

@Injectable({
    providedIn: 'root',
})
export class PostLoginService {
    private lastUserFetchTime = 0;
    private _subscriptions = new SubscriptionList() as SubscriptionListType;
    private readonly visibilityHandler = this.onVisibilityChange.bind(this);

    private readonly authService = inject(AuthService);
    private readonly userService = inject(UserService);
    private readonly wsService = inject(WebsocketService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly userDataService = inject(UserDataService);
    private readonly tenantService = inject(TenantService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly sessionTimeoutService = inject(SessionTimeoutService);
    private readonly configurationsService = inject(ConfigurationsService);

    init() {
        this.listenDocumentVisibility();
        this.listenNotification();
        this.setOidcToken();
        this.setTenant();
        this.setWebConfig();
        this.setFeatureFlags();
        this.startWatching();
    }

    destroy() {
        this._subscriptions.unsubscribeAllSafe();
        document.removeEventListener(
            'visibilitychange',
            this.visibilityHandler,
        );
    }

    private listenDocumentVisibility() {
        document.addEventListener('visibilitychange', this.visibilityHandler);
    }

    private onVisibilityChange(): void {
        if (!document.hidden) {
            const now = Date.now();

            if (now - this.lastUserFetchTime >= 5 * 60 * 1000) {
                this.setUser();
                this.lastUserFetchTime = now;
            }
        }
    }

    private setUser() {
        this._subscriptions['set-user'] = this.userService
            .fetchUser()
            .subscribe();
    }

    private listenNotification() {
        this._subscriptions['listen-notification'] =
            this.wsService.notification$.subscribe({
                next: (res) => {
                    if (res)
                        this.snackbarService.success({
                            header: 'Notification',
                            content: res.message,
                        });
                },
            });
    }

    private setOidcToken() {
        this._subscriptions['set-oidc-token'] = this.authService
            .setOidcToken()
            .subscribe({
                next: (token) => {
                    if (token) {
                        this.wsService.init();
                    }
                },
            });
    }

    private setTenant() {
        this._subscriptions['set-tenant'] = this.tenantService
            .getMineTenant()
            .subscribe();
    }

    private setWebConfig() {
        this._subscriptions['set-web-config'] = this.configurationsService
            .getWebConfig()
            .subscribe({
                next: ({ ahaAppId: appId }) =>
                    this.initializeFeedbackWidget({ appId }),
            });
    }

    private setFeatureFlags() {
        this._subscriptions['set-feature-flags'] = this.featureFlagService
            .getFeatureFlags()
            .subscribe();
    }

    private startWatching() {
        this.sessionTimeoutService.startWatching();
    }

    private initializeFeedbackWidget(params: { appId: string }) {
        this._subscriptions['user'] = this.userDataService.user$.subscribe({
            next: (user) => {
                if (user) {
                    this.loadScript({
                        userId: user.rmsId.toString(),
                        name: user.userName,
                        email: user.email,
                        appId: params.appId,
                    });
                }
            },
        });
    }

    private loadScript(p: {
        userId: string;
        name: string;
        email: string;
        appId: string;
    }) {
        if (window.aha) {
            window.aha('initialize', {
                account: 'geaero',
                applicationId: p.appId,
                user: {
                    id: p.userId,
                    name: p.name,
                    email: p.email,
                },
            });

            this._subscriptions.unsubscribeSafe('user');
        }
    }
}
